import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Check, CreditCard, Shield, Zap, Crown, 
  ArrowRight, Loader2, CheckCircle, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 9.90,
    period: 'monat',
    description: 'Perfekt für den Einstieg',
    features: [
      'Bis zu 50 Dokumente/Monat',
      'KI-Dokumentenerkennung',
      'Kanban-Board',
      'E-Mail Support'
    ],
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
    popular: false
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    period: 'monat',
    description: 'Für wachsende Unternehmen',
    features: [
      'Unbegrenzte Dokumente',
      'KI-Dokumentenerkennung',
      'Automatische Sortierung',
      'Steuerberater-Export',
      'Kalender-Integration',
      'Prioritäts-Support'
    ],
    icon: Crown,
    color: 'from-violet-500 to-purple-600',
    popular: true
  },
  {
    id: 'business',
    name: 'Business',
    price: 49,
    period: 'monat/nutzer',
    description: 'Für Teams & Unternehmen',
    features: [
      'Alles aus Pro',
      'Multi-User (bis 10)',
      'Multi-Company',
      'API-Zugang',
      'Dedizierter Support',
      'On-Premise Option'
    ],
    icon: Shield,
    color: 'from-amber-500 to-orange-500',
    popular: false
  }
];

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [redirectingToStripe, setRedirectingToStripe] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    const preselectedPlan = searchParams.get('plan');
    
    // Also check localStorage for plan saved during signup
    const savedPlan = localStorage.getItem('selected_plan');
    
    if (sessionId && paymentStatus === 'success') {
      handlePaymentSuccess(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Zahlung abgebrochen. Wähle einen Plan um fortzufahren.');
      // Clear saved plan so user can reselect
      localStorage.removeItem('selected_plan');
    } else if (savedPlan && ['starter', 'pro', 'business'].includes(savedPlan)) {
      // If coming from signup with saved plan - go directly to Stripe!
      setSelectedPlan(savedPlan);
      localStorage.removeItem('selected_plan'); // Clear it
      // Automatically redirect to Stripe checkout
      handleDirectCheckout(savedPlan);
    } else if (preselectedPlan && ['starter', 'pro', 'business'].includes(preselectedPlan)) {
      // If coming with a preselected plan from URL
      setSelectedPlan(preselectedPlan);
    }
  }, [token, searchParams, navigate]);

  const handleDirectCheckout = async (planId) => {
    setRedirectingToStripe(true);
    try {
      const plan = plans.find(p => p.id === planId);
      const response = await axios.post(`${API_URL}/api/subscription/create-checkout`, {
        plan_id: planId,
        price: plan.price
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Direct checkout error:', error);
      toast.error('Fehler beim Starten der Zahlung');
      setRedirectingToStripe(false);
    }
  };

  const handlePaymentSuccess = async (sessionId) => {
    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API_URL}/api/subscription/verify?session_id=${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'paid') {
        toast.success('Zahlung erfolgreich! Willkommen bei JaySmart!');
        await refreshUser();
        // Go directly to onboarding
        navigate('/onboarding');
      } else {
        toast.error('Zahlung konnte nicht bestätigt werden.');
        setCheckingPayment(false);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Fehler bei der Zahlungsbestätigung');
      setCheckingPayment(false);
    }
  };

  const handleCheckout = async (planId) => {
    setLoading(true);
    setSelectedPlan(planId);
    
    try {
      const plan = plans.find(p => p.id === planId);
      const response = await axios.post(`${API_URL}/api/subscription/create-checkout`, {
        plan_id: planId,
        price: plan.price
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Fehler beim Starten der Zahlung');
      setLoading(false);
    }
  };

  if (checkingPayment || redirectingToStripe) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-violet-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">
            {checkingPayment ? 'Zahlung wird überprüft...' : 'Weiterleitung zu Stripe...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          {user?.email_verified && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <CheckCircle className="w-4 h-4" />
              E-Mail bestätigt
            </motion.div>
          )}
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Wähle deinen Plan
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/60 max-w-2xl mx-auto"
          >
            30 Tage Geld-zurück-Garantie • Jederzeit kündbar
          </motion.p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, index) => {
            const isSelected = selectedPlan === plan.id;
            return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative rounded-2xl p-6 cursor-pointer transition-all ${
                isSelected
                  ? 'bg-gradient-to-b from-violet-500/30 to-purple-600/20 border-2 border-violet-400 scale-105 ring-2 ring-violet-400/50' 
                  : plan.popular 
                    ? 'bg-gradient-to-b from-violet-500/20 to-purple-600/10 border-2 border-violet-500/50' 
                    : 'bg-white/5 border border-white/10 hover:border-white/30'
              }`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              {isSelected && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1">
                    <Check className="w-3 h-3" /> AUSGEWÄHLT
                  </span>
                </div>
              )}
              {!isSelected && plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-violet-500 to-purple-600 text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" /> EMPFOHLEN
                  </span>
                </div>
              )}
              
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                <plan.icon className="w-6 h-6 text-white" />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
              <p className="text-white/50 text-sm mb-4">{plan.description}</p>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">{plan.price}€</span>
                <span className="text-white/50">/{plan.period}</span>
              </div>
              
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={() => handleCheckout(plan.id)}
                disabled={loading}
                className={`w-full ${
                  isSelected
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white'
                    : plan.popular
                      ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
                data-testid={`checkout-${plan.id}-btn`}
              >
                {loading && selectedPlan === plan.id ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                {loading && selectedPlan === plan.id ? 'Wird geladen...' : isSelected ? 'Jetzt kaufen' : 'Auswählen'}
              </Button>
            </motion.div>
          );
          })}
        </div>

        {/* Trust Badges */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap justify-center items-center gap-8 mt-12 text-white/40 text-sm"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            SSL-verschlüsselt
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Sichere Zahlung via Stripe
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            DSGVO-konform
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CheckoutPage;
